// src/store/modules/operator.js
import Vue from "vue";
import OperatorApi from "@/services/api/Operators";

/** Helper: izvuče data iz axios responsa, radi i za paginirane i "flat" odgovore */
// src/store/modules/operator.js

/** Helper: izvuče data iz axios responsa, radi i za paginirane i "flat" odgovore */
function pickData(res) {
  if (!res || !res.data) return null;
  const d = res.data;

  // NEW: operator_groups lista (paginirano)
  if (Array.isArray(d.operator_groups)) {
    return {
      items: d.operator_groups,
      meta: {
        count: d.objects_count ?? d.operator_groups.length,
        pages: d.pages_count ?? 1,
        next: d.links?.next ?? null,
        previous: d.links?.previous ?? null,
      },
    };
  }

  // Existing: space_operators lista (paginirano)
  if (Array.isArray(d.space_operators)) {
    return {
      items: d.space_operators,
      meta: {
        count: d.objects_count ?? d.space_operators.length,
        pages: d.pages_count ?? 1,
        next: d.links?.next ?? null,
        previous: d.links?.previous ?? null,
      },
    };
  }

  // Old shapes (keep compatibility)
  if (Array.isArray(d.results)) {
    return { items: d.results, meta: { count: d.count, next: d.next, previous: d.previous } };
  }
  if (Array.isArray(d)) {
    return { items: d, meta: null };
  }

  // Single item
  return { item: d, meta: null };
}

const stripSensitive = (op) => {
  if (!op || typeof op !== 'object') return op;
  const copy = { ...op };
  delete copy.password;
  return copy;
};




/** Helper: mapiranje forme u payload koji backend očekuje */
// Resolves the value to send for a given operator custom field index.
// If the form explicitly marks the field as disabled (operator_custom_N_enabled === false,
// as set by the Edit Operator form), the value is forced to empty so disabled
// fields can never have a value sent to the backend - even if stale/typed-in
// data is still sitting in the form state. When the enabled flag isn't present
// at all (e.g. the Create Operator form, which only renders inputs for fields
// that are already enabled), this falls through to the normal value untouched.
function resolveCustomFieldValue(form, idx) {
  const enabledKey = `operator_custom_${idx}_enabled`;
  if (form[enabledKey] === false) return "";
  return form[`customField${idx}`] || form[`custom_${idx}`] || "";
}

export function mapFormToOperatorPayload(form, { includeCredentials = false, createdByEmail } = {}) {
  const payload = {
    username: form.username || "",
    first_name: form.first_name || form.firstName || "",
    last_name: form.last_name || form.lastName || "",
    email: form.email || "",
    date_of_birth: form.dateOfBirth || form.date_of_birth ? new Date(form.dateOfBirth || form.date_of_birth).toISOString() : null,
    mobile_phone: form.phoneNumber || form.mobile_phone || "",
    enabled: typeof form.enabled === "boolean" ? form.enabled : true,
    gender: form.gender || "",
    vision_id: form.visionId || form.vision_id || "",
    external_id: form.externalId || form.external_id || "",
    operator_group_uuids: (form.operator_group_uuids || form.operator_groups || form.operatorGroups || []).map(g => g.uuid || g),
    location_uuids: (form.locations || form.location_uuids || []).map(l => l.uuid || l),
    tag_uuids: (form.tag_uuids || form.tags || []).map(t => t.uuid || t),
    operator_custom_1: resolveCustomFieldValue(form, 1),
    operator_custom_2: resolveCustomFieldValue(form, 2),
    operator_custom_3: resolveCustomFieldValue(form, 3),
    operator_custom_4: resolveCustomFieldValue(form, 4),
    operator_custom_5: resolveCustomFieldValue(form, 5),
    operator_custom_6: resolveCustomFieldValue(form, 6),
    operator_custom_7: resolveCustomFieldValue(form, 7),
    operator_custom_8: resolveCustomFieldValue(form, 8),
  };

  if (includeCredentials) {
    payload.password = form.password || "";
  }
  if (createdByEmail) {
    payload.created_by_email = createdByEmail;
  }
  return payload;
}

const state = () => ({
  // collections
  operators: [],
  operatorsMeta: null,
  groups: [],
  groupsMeta: null,

  // details
  operator: null,
  groupDetail: null,

  // membership
  groupMembers: [],           // lista članova za "trenutnu" grupu
  groupMembersMeta: null,
  currentGroupUuid: null,

  // custom _s
  custom_s: null,
  spaceConfig: null, // Raw space configuration data
  
  // permissions
  permissions: [],
  permissionsMeta: null,
  // Combined permissions per group
  groupPermissionsPerGroup: {},

  // flags
  loading: {
    operators: false,
    operator: false,
    groups: false,
    groupMembers: false,
    creating: false,
    updating: false,
    resettingCreds: false,
    keycloak: false,
    custom_s: false,
    permissions: false,
  },
  error: {
    operators: null,
    operator: null,
    groups: null,
    groupMembers: null,
    creating: null,
    updating: null,
    resettingCreds: null,
    keycloak: null,
    custom_s: null,
    permissions: null,
  },
});

const getters = {
  getOperators: (state) => state.operators,
  getOperatorsMeta: (state) => state.operatorsMeta,
  getOperator: (s) => s.operator,
  getGroups: (state) => state.groups,
  getGroupDetail: (state) => state.groupDetail,
  getGroupsMeta: (s) => s.groupsMeta,
  getGroupMembers: (s) => s.groupMembers,
  getGroupMembersMeta: (s) => s.groupMembersMeta,
  getCurrentGroupUuid: (s) => s.currentGroupUuid,
  getCustom_s: (s) => s.custom_s,
  getSpaceConfig: (s) => s.spaceConfig,
  getPermissions: (s) => s.permissions,
  getPermissionsMeta: (s) => s.permissionsMeta,
  getGroupPermissionsPerGroup: (s) => (groupUuid) => {
    return s.groupPermissionsPerGroup[groupUuid] || [];
  },
  getOperatorCustom_s: (state) => {
    if (!state.spaceConfig) return [];
    
    const custom_s = [];
    for (let i = 1; i <= 8; i++) {
      const alias = state.spaceConfig[`operator_custom_${i}_alias`];
      const enabled = state.spaceConfig[`operator_custom_${i}_enabled`];
      const required = state.spaceConfig[`operator_custom_${i}_required`];
      const unique = state.spaceConfig[`operator_custom_${i}_unique`];
      
      // Only include enabled _s
      if (enabled === true || enabled === 'true' || enabled === 1 || enabled === '1') {
        custom_s.push({
          index: i,
          alias: alias || `Custom _ ${i}`,
          enabled: true,
          required: required === true || required === 'true',
          unique: unique === true || unique === 'true'
        });
      }
    }
    
    return custom_s;
  },
  isLoading: (s) => s.loading,
  getErrors: (s) => s.error,
};

const mutations = {
  SET_OPERATORS(state, { items, meta }) { state.operators = items || []; state.operatorsMeta = meta || null; },
  SET_OPERATOR(state, item) { state.operator = item || null; },
  SET_GROUPS(state, { items, meta }) { state.groups = items || []; state.groupsMeta = meta || null; },
  SET_GROUP_DETAIL(state, item) { state.groupDetail = item || null; },
  SET_GROUP_MEMBERS(state, { items, meta, groupUuid }) {
    state.groupMembers = items || [];
    state.groupMembersMeta = meta || null;
    state.currentGroupUuid = groupUuid || null;
  },
  SET_CUSTOM__S(state, payload) { state.custom_s = payload || null; },
  SET_SPACE_CONFIG(state, payload) { state.spaceConfig = payload || null; },
  SET_LOADING(state, { key, val }) { state.loading[key] = !!val; },
  SET_ERROR(state, { key, val }) { state.error[key] = val || null; },
  // optimistične izmjene (opcionalno)
  PUSH_OPERATOR(state, item) { state.operators = [item, ...state.operators]; },
  REPLACE_OPERATOR(state, item) {
    const i = state.operators.findIndex(x => x.uuid === item.uuid);
    if (i >= 0) state.operators.splice(i, 1, item);
  },
  UPDATE_OPERATOR(state, { uuid, fields }) {
    if (!uuid || !Array.isArray(fields)) return;

    const applyFields = (target) => {
      if (!target) return;
      fields.forEach(({ key, value }) => {
        Vue.set(target, key, value);
      });
    };

    const listIndex = state.operators.findIndex((x) => x.uuid === uuid);
    if (listIndex >= 0) {
      applyFields(state.operators[listIndex]);
      Vue.set(state.operators, listIndex, state.operators[listIndex]);
    }

    if (state.operator && state.operator.uuid === uuid) {
      applyFields(state.operator);
    }

    const memberIndex = state.groupMembers.findIndex((x) => x.uuid === uuid);
    if (memberIndex >= 0) {
      applyFields(state.groupMembers[memberIndex]);
      Vue.set(state.groupMembers, memberIndex, state.groupMembers[memberIndex]);
    }
  },
  CLEAR_SELECTED_OPERATOR(state) {
    state.operator = null;
  },
  SET_PERMISSIONS(state, { items, meta }) {
    state.permissions = items || [];
    state.permissionsMeta = meta || null;
  },

  SET_GROUP_PERMISSIONS_PER_GROUP(state, { groupUuid, permissions }) {
    state.groupPermissionsPerGroup = {
      ...state.groupPermissionsPerGroup,
      [groupUuid]: permissions
    };
  },
};
const actions = {
  /* ========== OPERATORS ========== */

 async fetchOperators({ commit }, qs) {
    commit("SET_LOADING", { key: "operators", val: true });
    commit("SET_ERROR", { key: "operators", val: null });
    try {
      // keep your default if none provided
      qs = qs || "page_size=36&page=1";
      const res = await OperatorApi.getOperators(qs);
      const { items = [], meta = null } = pickData(res) || {};
      const safeItems = items.map(stripSensitive);
      commit("SET_OPERATORS", { items: safeItems, meta });
      return safeItems;
    } catch (e) {
      commit("SET_ERROR", { key: "operators", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "operators", val: false });
    }
  },

  async fetchOperator({ commit }, operator_uuid) {
    commit("SET_LOADING", { key: "operator", val: true });
    commit("SET_ERROR", { key: "operator", val: null });
    try {
      const res = await OperatorApi.getOperator(operator_uuid);
      const { item } = pickData(res) || {};
      const operatorData = item || res.data;
      commit("SET_OPERATOR", operatorData);
      return operatorData;
    } catch (e) {
      commit("SET_ERROR", { key: "operator", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "operator", val: false });
    }
  },

  updateOperatorPresence({ commit, state }, { uuid, fields }) {
    commit("UPDATE_OPERATOR", { uuid, fields });
    const fromList = state.operators.find((x) => x.uuid === uuid);
    if (fromList) return Promise.resolve(fromList);
    if (state.operator && state.operator.uuid === uuid) {
      return Promise.resolve(state.operator);
    }
    return Promise.resolve({ uuid });
  },

  async createOperator({ commit }, { form, createdByEmail }) {
    commit("SET_LOADING", { key: "creating", val: true });
    commit("SET_ERROR", { key: "creating", val: null });
    try {
      const payload = mapFormToOperatorPayload(form, { includeCredentials: true, createdByEmail });
      const res = await OperatorApi.addOperator(payload);
      const { item } = pickData(res) || {};
      if (item) commit("PUSH_OPERATOR", item);
      return item || res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "creating", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "creating", val: false });
    }
  },

  async updateOperator({ commit }, { operator_uuid, form }) {

    commit("SET_LOADING", { key: "updating", val: true });
    commit("SET_ERROR", { key: "updating", val: null });
    try {
      const payload = mapFormToOperatorPayload(form);
      const res = await OperatorApi.updateOperator(operator_uuid, payload);
      const { item } = pickData(res) || {};
      if (item) {
        commit("REPLACE_OPERATOR", item);
        commit("SET_OPERATOR", item);
      }
      return item || res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "updating", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "updating", val: false });
    }
  },

  async resetOperatorCredentials({ commit }, { operator_uuid, payload }) {
    commit("SET_LOADING", { key: "resettingCreds", val: true });
    commit("SET_ERROR", { key: "resettingCreds", val: null });
    try {
      const res = await OperatorApi.resetOperatorCredentials(operator_uuid, payload);
      return res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "resettingCreds", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "resettingCreds", val: false });
    }
  },

  async updateOperatorKeycloakAccount({ commit }, { operator_uuid, keycloak_account_id }) {
    commit("SET_LOADING", { key: "keycloak", val: true });
    commit("SET_ERROR", { key: "keycloak", val: null });
    try {
      const res = await OperatorApi.updateOperatorKeycloakAccount(operator_uuid, keycloak_account_id);
      return res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "keycloak", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "keycloak", val: false });
    }
  },

  /* ========== GROUPS ========== */

  async fetchOperatorGroups({ commit }, qs = { page_size: 36, page: 1 }) {
    commit("SET_LOADING", { key: "groups", val: true });
    commit("SET_ERROR", { key: "groups", val: null });
    try {
      const res = await OperatorApi.getOperatorGroups(qs);
      const { items = [], meta = null } = pickData(res) || {};
      commit("SET_GROUPS", { items, meta });
      return items;
    } catch (e) {
      commit("SET_ERROR", { key: "groups", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "groups", val: false });
    }
  },

  async fetchOperatorGroup({ commit }, operator_group_uuid) {
    commit("SET_LOADING", { key: "groups", val: true });
    commit("SET_ERROR", { key: "groups", val: null });
    try {
      const res = await OperatorApi.getOperatorGroup(operator_group_uuid);
      const { item } = pickData(res) || {};
      commit("SET_GROUP_DETAIL", item);
      return item;
    } catch (e) {
      commit("SET_ERROR", { key: "groups", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "groups", val: false });
    }
  },
  async createOperatorGroup({ dispatch, commit }, payload) {
    commit("SET_LOADING", { key: "updating", val: true });
    commit("SET_ERROR", { key: "updating", val: null });
    try {
      const res = await OperatorApi.addOperatorGroup(payload);
      // osvježi listu
      await dispatch("fetchOperatorGroups");
      return res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "updating", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "updating", val: false });
    }
  },
  async updateOperatorGroup({ dispatch, commit }, { operator_group_uuid, payload }) {
    commit("SET_LOADING", { key: "updating", val: true });
    commit("SET_ERROR", { key: "updating", val: null });
    try {
      const res = await OperatorApi.updateOperatorGroup(operator_group_uuid, payload);
      await dispatch("fetchOperatorGroups");
      return res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "updating", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "updating", val: false });
    }
  },

  /* ========== GROUP MEMBERSHIP ========== */
  async fetchOperatorsInGroup({ commit }, { operator_group_uuid, qs }) {
    commit("SET_LOADING", { key: "groupMembers", val: true });
    commit("SET_ERROR", { key: "groupMembers", val: null });
    try {
      const res = await OperatorApi.getOperatorsInGroup(operator_group_uuid, qs);
      const { items, meta } = pickData(res) || {};
      commit("SET_GROUP_MEMBERS", { items, meta, groupUuid: operator_group_uuid });
      return items;
    } catch (e) {
      commit("SET_ERROR", { key: "groupMembers", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "groupMembers", val: false });
    }
  },

  async addOperatorsToGroup({ dispatch, commit }, { operator_group_uuid, space_operators }) {
    commit("SET_LOADING", { key: "groupMembers", val: true });
    commit("SET_ERROR", { key: "groupMembers", val: null });
    try {
      const res = await OperatorApi.addOperatorsToGroup(operator_group_uuid, space_operators);
      // refresh članova iste grupe
      await dispatch("fetchOperatorsInGroup", { operator_group_uuid, qs: { page: 1 } });
      return res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "groupMembers", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "groupMembers", val: false });
    }
  },

  async removeOperatorFromGroup({ dispatch, commit }, { operator_group_uuid, operator_uuid }) {
    commit("SET_LOADING", { key: "groupMembers", val: true });
    commit("SET_ERROR", { key: "groupMembers", val: null });
    try {
      const res = await OperatorApi.removeOperatorFromGroup(operator_group_uuid, operator_uuid);
      await dispatch("fetchOperatorsInGroup", { operator_group_uuid, qs: { page: 1 } });
      return res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "groupMembers", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "groupMembers", val: false });
    }
  },

  /* ========== CUSTOM _S ========== */

  async fetchSpaceCustom_s({ commit }) {
    commit("SET_LOADING", { key: "custom_s", val: true });
    commit("SET_ERROR", { key: "custom_s", val: null });
    try {
      // Koristimo locations API jer space config sadrži sve custom _s
      const LocationsApi = await import("@/services/api/Locations");
      const res = await LocationsApi.default.getSpace();
      const { item } = pickData(res) || {};
      commit("SET_SPACE_CONFIG", item || res.data);
      return item || res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "custom_s", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "custom_s", val: false });
    }
  },

  async patchOperatorCustom_s({ commit }, payload) {
    commit("SET_LOADING", { key: "custom_s", val: true });
    commit("SET_ERROR", { key: "custom_s", val: null });
    try {
      const res = await OperatorApi.patchOperatorCustom_s(payload);
      // backend obično vrati novo stanje custom polja:
      const { item } = pickData(res) || {};
      commit("SET_CUSTOM__S", item || res.data);
      return item || res.data;
    } catch (e) {
      commit("SET_ERROR", { key: "custom_s", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "custom_s", val: false });
    }
  },

  /* ========== PERMISSIONS ========== */

  async fetchPermissions({ commit }) {
    commit("SET_LOADING", { key: "permissions", val: true });
    commit("SET_ERROR", { key: "permissions", val: null });
    try {
      const res = await OperatorApi.getPermissions();
      // Handle different response formats
      let items = [];
      let meta = null;
      if (res.data) {
        if (Array.isArray(res.data.items)) {
          items = res.data.items;
          meta = res.data.meta || null;
        } else if (Array.isArray(res.data.results)) {
          items = res.data.results;
          meta = { count: res.data.count || items.length };
        } else if (Array.isArray(res.data)) {
          items = res.data;
          meta = { count: items.length };
        } else {
          items = [res.data];
          meta = { count: 1 };
        }
      }
      commit("SET_PERMISSIONS", { items, meta });
      return items;
    } catch (e) {
      console.error(' Store: Permissions fetch failed:', e);
      commit("SET_ERROR", { key: "permissions", val: e });
      throw e;
    } finally {
      commit("SET_LOADING", { key: "permissions", val: false });
    }
  },

  /**
   * Combines global permissions and group-specific claims for a group
   * @param {Object} context - Vuex context
   * @param {Object} payload - { groupUuid, groupClaims }
   */
  async combinePermissionsForGroup({ commit, state }, { groupUuid, groupClaims }) {
    // groupClaims: array of claim UUIDs for the group
    // state.permissions: global permissions array
    // Build combined permissions structure
    // Mark claims as selected if in groupClaims
    const combined = (state.permissions || []).map(category => {
      return {
        ...category,
        operator_permission_groups: (category.operator_permission_groups || []).map(group => {
          return {
            ...group,
            operator_permission_claims: (group.operator_permission_claims || []).map(claim => {
              return {
                ...claim,
                selected: groupClaims.includes(claim.operator_permission_claim_uuid)
              };
            })
          };
        })
      };
    });
    commit('SET_GROUP_PERMISSIONS_PER_GROUP', { groupUuid, permissions: combined });
    return combined;
  },

  // Legacy action name for backwards compatibility
  async fetchPermissionsCategoryGroupClaim({ dispatch }) {
    return await dispatch('fetchPermissions');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};






